import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createFilter,
  deleteFilter,
  getFilters,
  updateFilter,
  type SavedFilter,
} from "../api/client";
import { useAuth } from "../context/AuthContext";

function FilterForm({
  initial,
  onSave,
  onCancel,
}: {
  initial?: SavedFilter;
  onSave: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [alertsEnabled, setAlertsEnabled] = useState(initial?.alerts_enabled ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (initial) {
        await updateFilter(initial.id, { name, alerts_enabled: alertsEnabled });
      } else {
        await createFilter({ name, filter_json: {}, alerts_enabled: alertsEnabled });
      }
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save.");
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 border rounded p-4 space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="My filter"
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
        <input
          type="checkbox"
          checked={alertsEnabled}
          onChange={(e) => setAlertsEnabled(e.target.checked)}
          className="rounded"
        />
        Enable daily digest alerts
      </label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-1.5 rounded disabled:opacity-50"
        >
          {saving ? "Saving…" : initial ? "Update" : "Create"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-gray-600 hover:text-gray-900 px-4 py-1.5 border rounded"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function Filters() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<SavedFilter[]>([]);
  const [fetchError, setFetchError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { replace: true });
    }
  }, [user, loading, navigate]);

  function loadFilters() {
    if (!user) return;
    getFilters()
      .then(setFilters)
      .catch((err: unknown) => {
        setFetchError(err instanceof Error ? err.message : "Failed to load filters.");
      });
  }

  useEffect(() => {
    loadFilters();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete(id: string) {
    if (!window.confirm("Delete this filter?")) return;
    try {
      await deleteFilter(id);
      loadFilters();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Delete failed.");
    }
  }

  if (loading) return <div className="p-8 text-gray-500">Loading…</div>;
  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold">Saved filters</h1>
        <Link to="/" className="text-sm text-blue-600 hover:underline">
          ← Inventory
        </Link>
      </div>

      {fetchError && <p className="text-sm text-red-600 mb-4">{fetchError}</p>}

      <div className="space-y-3 mb-6">
        {filters.length === 0 && !showCreate && (
          <p className="text-gray-500 text-sm">No filters yet.</p>
        )}
        {filters.map((f) =>
          editing === f.id ? (
            <FilterForm
              key={f.id}
              initial={f}
              onSave={() => {
                setEditing(null);
                loadFilters();
              }}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div
              key={f.id}
              className="border rounded p-4 flex items-center justify-between bg-white"
            >
              <div>
                <p className="font-medium text-sm">{f.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {f.alerts_enabled ? "Alerts: daily digest" : "Alerts: off"}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(f.id)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(f.id)}
                  className="text-sm text-red-600 hover:underline"
                >
                  Delete
                </button>
              </div>
            </div>
          ),
        )}
      </div>

      {showCreate ? (
        <FilterForm
          onSave={() => {
            setShowCreate(false);
            loadFilters();
          }}
          onCancel={() => setShowCreate(false)}
        />
      ) : (
        <button
          onClick={() => setShowCreate(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded"
        >
          New filter
        </button>
      )}
    </div>
  );
}
